import {Plural, Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {usePrevious} from '../../../../../srcCommon/helpers/hookHelpers';
import {isJobRunning} from '../../../../helpers/jobHelpers';
import {CrmSyncModel, CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';
import {JobStatusesEnum, JobTypesEnum, SyncJobModel} from '../../../../models/api/jobModel';
import {onViewErrorsClick} from '../../../accounts/crm/common/crmSyncHelpers';
import {HubspotMaxAllAccounts} from '../../../accounts/crm/hubspot/hubspotHelpers';
import {Banner, BannerTypesEnum} from '../../../common/banners/banner';
import {BannerCloseAction} from '../../../common/banners/bannerCloseAction';
import {BannerContent} from '../../../common/banners/bannerContent';
import {BannerContentTitle} from '../../../common/banners/bannerContentTitle';
import {BannerIcon} from '../../../common/banners/bannerIcon';
import {BannerProgress} from '../../../common/banners/bannerProgress';
import {BannerPrioritiesEnum} from '../../../common/banners/bannersContext';
import {BannerSecondaryText} from '../../../common/banners/bannerSecondaryText';
import {BannerText} from '../../../common/banners/bannerText';

/*
 * Props.
 */

interface SyncJobBannerProps {
  job: SyncJobModel;
  crmSync: CrmSyncModel;
  progress: number;
  hasCrmSyncFullPageSalesforce: boolean;
  navigateToLink: HandlerOf<string>;
  tableForceRefresh: Handler;
}

/*
 * Style.
 */

const StyledBannerWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
`;

const StyledBannerMessageDiv = styled.div`
  font-size: 12px;
  width: 205px;
`;

/*
 * Component.
 */

export const SyncJobBanner: FC<SyncJobBannerProps> = (props) => {
  const {job, progress, crmSync, hasCrmSyncFullPageSalesforce, navigateToLink, tableForceRefresh} = props;
  const isImporting = progress < 1 && isJobRunning(job);
  // If we load the job and it is not in a running state, we should not display it.
  const [shouldHideErrorBanner, setShouldHideErrorBanner] = useState(!isJobRunning(job));
  const [shouldHideSuccessBanner, setShouldHideSuccessBanner] = useState(!isJobRunning(job));
  const [shouldHideSuccessErrorsBanner, setShouldHideSuccessErrorsBanner] = useState(!isJobRunning(job));

  const onDismissErrorClick = () => setShouldHideErrorBanner(true);
  const onDismissSuccessClick = () => setShouldHideSuccessBanner(true);
  const onDismissSuccessErrorsClick = () => setShouldHideSuccessErrorsBanner(true);
  const onNavigateToErrorsClick = () => {
    setShouldHideSuccessErrorsBanner(true);
    onViewErrorsClick(crmSync.source, hasCrmSyncFullPageSalesforce, navigateToLink);
  };

  const previousJobStatus = usePrevious(job.status);

  const isAccountJob = job.type === JobTypesEnum.SYNC_ACCOUNTS;

  useEffect(() => {
    if (!previousJobStatus) {
      return;
    }

    if (previousJobStatus !== JobStatusesEnum.DONE && job.status === JobStatusesEnum.DONE) {
      tableForceRefresh();
    }
  });

  if (job.status === JobStatusesEnum.FAILED) {
    return renderErrorBanner(isAccountJob, shouldHideErrorBanner, onDismissErrorClick);
  }
  if (isImporting) {
    return renderProgressBanner(isAccountJob, progress);
  }

  return renderCompletedBanner(
    job,
    crmSync,
    shouldHideSuccessBanner,
    shouldHideSuccessErrorsBanner,
    onDismissSuccessClick,
    onDismissSuccessErrorsClick,
    onNavigateToErrorsClick,
  );
};

/*
 * Helpers.
 */

function renderProgressBanner(isAccountJob: boolean, progress: number) {
  const content = isAccountJob ? (
    <Trans id="banner-sync-accounts-syncing">Syncing Accounts...</Trans>
  ) : (
    <Trans id="banner-sync-contacts-syncing">Syncing Contacts...</Trans>
  );
  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.INFORMATION}>
      <BannerProgress progress={progress} />
      <BannerIcon iconName="repeatCircleSmall" shouldRotate />
      <BannerText>{content}</BannerText>
      <BannerSecondaryText>
        <Trans id="banner-sync-progress">{Math.floor(100 * progress)}%</Trans>
      </BannerSecondaryText>
    </Banner>
  );
}

function renderCompletedBanner(
  job: SyncJobModel,
  crmSync: CrmSyncModel,
  shouldHideSuccessBanner: boolean,
  shouldHideSuccessErrorsBanner: boolean,
  onDismissSuccessClick: Handler,
  onDismissSuccessErrorsClick: Handler,
  onNavigateToErrorsClick: Handler,
) {
  const isAccountJob = job.type === JobTypesEnum.SYNC_ACCOUNTS;

  const shouldShowLimitReachedBanner =
    isAccountJob &&
    crmSync.source === CrmSyncSourcesEnum.HUBSPOT &&
    job.completedStepCount >= HubspotMaxAllAccounts;
  return (
    <StyledBannerWrapperDiv>
      {renderImportTotalSuccessBanner(
        isAccountJob,
        job.metadata?.syncSucceeded ?? 0,
        shouldShowLimitReachedBanner,
        shouldHideSuccessBanner,
        onDismissSuccessClick,
      )}
    </StyledBannerWrapperDiv>
  );
}

function renderImportTotalSuccessBanner(
  isAccountJob: boolean,
  imported: number,
  shouldShowLimitReachedBanner: boolean,
  shouldHideSuccessBanner: boolean,
  onDismissClick: Handler,
) {
  if (shouldHideSuccessBanner || imported === 0) {
    return null;
  }

  if (shouldShowLimitReachedBanner) {
    return renderMaxAccountsSyncedBanner(onDismissClick);
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.INLINE}
          priority={BannerPrioritiesEnum.INFORMATION}
          color={theme.palette.green.shade40}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="checkmarkCircleSmall" />
          <BannerText>{renderSyncedSuccessfullyMessage(isAccountJob, imported)}</BannerText>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}

function renderSyncedSuccessfullyMessage(isAccountJob: boolean, numImported: number) {
  if (isAccountJob) {
    return (
      <Plural
        id="banner-sync-accounts-successful"
        value={numImported}
        one="Account synced successfully"
        other="Accounts synced successfully"
      />
    );
  }
  return (
    <Plural
      id="banner-sync-contacts-successful"
      value={numImported}
      one="Contact synced successfully"
      other="Contacts synced successfully"
    />
  );
}

function renderMaxAccountsSyncedBanner(onDismissClick: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          priority={BannerPrioritiesEnum.INFORMATION}
          color={theme.palette.green.shade40}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="checkmarkCircleSmall" />

          <BannerContent>
            <BannerContentTitle>
              <Trans id="banner-sync-account-100000-successful">100,000 accounts synced </Trans>
            </BannerContentTitle>
            <StyledBannerMessageDiv>
              <Trans id="banner-sync-account-100000-successful-message">
                Account sync limit reached: only the first 100,000 accounts synced.
              </Trans>
            </StyledBannerMessageDiv>
          </BannerContent>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}

function renderErrorBanner(isAccountJob: boolean, shouldHideErrorBanner: boolean, onDismissClick: Handler) {
  if (shouldHideErrorBanner) {
    return null;
  }

  const content = isAccountJob ? (
    <Trans id="banner-sync-accounts-failed">Accounts sync failed.</Trans>
  ) : (
    <Trans id="banner-sync-contacts-failed">Contacts sync failed.</Trans>
  );
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="warningFilledSmall" />
          <BannerContent>
            <BannerContentTitle>{content}</BannerContentTitle>
            <Trans id="banner-sync-failed-try-again-message">Please try again.</Trans>
          </BannerContent>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}
